import BackButton from 'Components/BackButton'
import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import CustomLoadingButton from 'Components/CustomLoadingButton'
import StyledTableHead from 'Components/StyledTableHead'
import { WithLoader } from 'Components/WithLoader'
import { DeliverDialog } from 'Services/SalesManagement/Features/Order/Components/DeliverDialog'
import { LogDialog } from 'Services/SalesManagement/Features/Order/Components/LogDialog'
import { WalletTransactionRefundDialog } from 'Services/SalesManagement/Features/Order/Components/WalletTransactionRefundDialog'
import { WebShopOrderLineRow } from 'Services/SalesManagement/Features/Order/Components/WebShopOrderLineRow'
import { WebShopOrderPaymentInfo } from 'Services/SalesManagement/Features/Order/Components/WebShopOrderPaymentInfo'
import {
  deliverWebShopOrder,
  fetchWebShopOrder,
  fetchWebShopOrderLineLog,
  fetchWebShopOrderReceipt,
  rollbackWalletTransaction
} from 'Services/SalesManagement/Features/Order/orderSlice'
import useTypedSelector, { useAppDispatch } from 'Store'
import { usePreviousLocation } from 'Util/Context/previousLocationContext'
import { SALES_MANAGEMENT_ROOT_URL } from 'Util/constants'
import fileDownload from 'Util/fileDownload'
import { datetimeFormatter, replaceHyphenWithSpace, showValueOrHyphen } from 'Util/formatters'
import SnackbarUtils from 'Util/Snackbar'
import DownloadIcon from '@mui/icons-material/CloudDownload'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import { unwrapResult } from '@reduxjs/toolkit'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { t } from 'ttag'

export default function WebShopOrderDetailPage() {
  const baseUrl = `${SALES_MANAGEMENT_ROOT_URL}/orders/webshop-orders`
  const previousLocation = usePreviousLocation(baseUrl)
  const dispatch = useAppDispatch()

  const instanceIdParam = parseInt(useParams<{ id: string }>().id as string, 10)
  const [isDeliverDialogOpen, setIsDeliverDialogOpen] = useState(false)
  const [isLogDialogOpen, setIsLogDialogOpen] = useState(false)
  const [isWalletTransactionRefundDialogOpen, setIsWalletTransactionRefundDialogOpen] =
    useState(false)
  const [walletTransactionId, setWalletTransactionId] = useState<string | null>(null)

  const {
    singleWebShopOrder,
    loading,
    webShopOrderLineLog: orderLineLog,
    webShopOrderLineLogLoading,
    walletTransactionRefundLoading,
    webShopOrderReceiptLoading
  } = useTypedSelector(state => state.order)

  useEffect(() => {
    const params = {
      id: instanceIdParam
    }
    dispatch(fetchWebShopOrder(params))
  }, [dispatch, instanceIdParam])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const handleDeliver = () => {
    const params = {
      id: instanceIdParam
    }
    dispatch(deliverWebShopOrder(params))
      .then(unwrapResult)
      .then(() => {
        setIsDeliverDialogOpen(false)
        SnackbarUtils.success(t`Order delivery successfully requested.`)
        dispatch(fetchWebShopOrder(params))
      })
      .catch(error => {
        console.error(error)
        SnackbarUtils.error(t`There was an error requesting the order delivery.`)
      })
  }

  const handleOpenLogClick = (id: number, line_pk: number) => {
    const params = {
      id,
      line_pk
    }
    dispatch(fetchWebShopOrderLineLog(params))
      .then(unwrapResult)
      .then(() => {
        setIsLogDialogOpen(true)
      })
      .catch(error => {
        SnackbarUtils.error(error.message)
      })
  }

  const handleReceiptDownload = (id: number) => {
    const params = {
      id
    }
    dispatch(fetchWebShopOrderReceipt(params))
      .then(unwrapResult)
      .then(response => {
        const tempLink = document.createElement('a')
        tempLink.href = window.URL.createObjectURL(response)
        const fileNamePrefix = t`Waltti webshop order receipt`
        const fileName = `${fileNamePrefix}, ${singleWebShopOrder?.transactional_id}.pdf`
        fileDownload(response, fileName)
      })
      .catch(error => {
        SnackbarUtils.error(error.error)
      })
  }

  const handleWalletTransactionRefundClick = (id: string) => {
    setWalletTransactionId(id)
    setIsWalletTransactionRefundDialogOpen(true)
  }

  const handleWalletTransactionRefund = () => {
    const walletTransactionRefundParams = {
      id: walletTransactionId as string
    }
    dispatch(rollbackWalletTransaction(walletTransactionRefundParams))
      .then(unwrapResult)
      .then(() => {
        setIsWalletTransactionRefundDialogOpen(false)
        SnackbarUtils.success(t`Wallet transaction successfully refunded.`)
        const params = {
          id: instanceIdParam
        }
        dispatch(fetchWebShopOrder(params))
      })
      .catch(error => {
        console.error(error)
        SnackbarUtils.error(t`There was an error refunding the wallet transaction.`)
      })
  }

  const breadcrumbs = [
    {
      title: t`Home`,
      link: SALES_MANAGEMENT_ROOT_URL
    },
    {
      title: t`Orders`
    },
    {
      title: t`Waltti webshop orders`,
      link: baseUrl
    },
    {
      title: singleWebShopOrder ? singleWebShopOrder.transactional_id : ''
    }
  ]

  const isLoading = loading === 'pending'
  const isLogLoading = webShopOrderLineLogLoading === 'pending'
  const isWalletTransactionRefundLoading = walletTransactionRefundLoading === 'pending'

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs items={breadcrumbs} />
        <Box>
          <BackButton label={t`Back`} link={previousLocation} />
          {singleWebShopOrder && (
            <>
              <CustomButton
                onClick={() => setIsDeliverDialogOpen(true)}
                sx={{ float: 'right', mb: 1, ml: 1 }}
              >{t`Request order delivery`}</CustomButton>
              <DeliverDialog
                open={isDeliverDialogOpen}
                handleClose={() => setIsDeliverDialogOpen(false)}
                handleSubmit={handleDeliver}
              />
              <CustomLoadingButton
                isLoading={webShopOrderReceiptLoading === 'pending'}
                onClick={() => handleReceiptDownload(singleWebShopOrder.id)}
                type="submit"
                icon={<DownloadIcon />}
                iconPosition="start"
                sx={{ float: 'right', mb: 1 }}
                size="medium"
              >{t`Download receipt`}</CustomLoadingButton>
            </>
          )}
        </Box>
      </Box>
      <WithLoader loading={isLoading}>
        <Box>
          {singleWebShopOrder && (
            <Box>
              <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table aria-label={t`Order details table`}>
                  <StyledTableHead>
                    <TableRow>
                      <TableCell colSpan={2}>{t`Order details`}</TableCell>
                    </TableRow>
                  </StyledTableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>{t`Transactional ID`}</TableCell>
                      <TableCell>{singleWebShopOrder.transactional_id}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{t`Contact email`}</TableCell>
                      <TableCell>{singleWebShopOrder.contact_email}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{t`Status`}</TableCell>
                      <TableCell>{replaceHyphenWithSpace(singleWebShopOrder.status)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{t`Created`}</TableCell>
                      <TableCell>{datetimeFormatter(singleWebShopOrder.created)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{t`Payment method`}</TableCell>
                      <TableCell>
                        {showValueOrHyphen(singleWebShopOrder.payment_method_name)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{t`Authority`}</TableCell>
                      <TableCell>{`${singleWebShopOrder.authority_name} (${singleWebShopOrder.authority_code})`}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{t`Commission`}</TableCell>
                      <TableCell>
                        {singleWebShopOrder.commission ? (
                          <>
                            {t`VAT`}: {singleWebShopOrder.commission.value} € ({t`VAT percentage`}:{' '}
                            {singleWebShopOrder.commission.vat_percentage} %)
                          </>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{t`Payment date`}</TableCell>
                      <TableCell>{datetimeFormatter(singleWebShopOrder.payment_date)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table aria-label={t`Order lines table`}>
                  <StyledTableHead>
                    <TableRow>
                      <TableCell colSpan={6}>{t`Order lines`}</TableCell>
                    </TableRow>
                  </StyledTableHead>
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell>{t`Travel card number`}</TableCell>
                      <TableCell>{t`Product`}</TableCell>
                      <TableCell>{t`Status`}</TableCell>
                      <TableCell>{t`Delivery message`}</TableCell>
                      <TableCell>{t`Log`}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {singleWebShopOrder.order_lines.length > 0 ? (
                      <>
                        <LogDialog
                          open={isLogDialogOpen}
                          handleClose={() => setIsLogDialogOpen(false)}
                          log={orderLineLog}
                        />
                        <WalletTransactionRefundDialog
                          open={isWalletTransactionRefundDialogOpen}
                          handleSubmit={handleWalletTransactionRefund}
                          handleClose={() => setIsWalletTransactionRefundDialogOpen(false)}
                          loading={isWalletTransactionRefundLoading}
                        />
                        {singleWebShopOrder.order_lines.map(line => (
                          <WebShopOrderLineRow
                            key={line.id}
                            orderId={singleWebShopOrder.id}
                            row={line}
                            handleOpenLogClick={handleOpenLogClick}
                            isLogLoading={isLogLoading}
                            handleWalletTransactionRefundClick={handleWalletTransactionRefundClick}
                            isWalletTransactionRefundLoading={isWalletTransactionRefundLoading}
                          />
                        ))}
                      </>
                    ) : (
                      <TableRow>
                        <TableCell>{t`No order lines available`}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <WebShopOrderPaymentInfo order={singleWebShopOrder} />
            </Box>
          )}
        </Box>
      </WithLoader>
    </Box>
  )
}
